import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import { useDebouncedValue } from '@mantine/hooks'
import Client from '../../models/client'
import { fetcherWithAuthPost } from '../api'
import { isFilterActive } from '../components/ColumnFilter'
import { loadFields } from '../utils/fieldStorage'
import { FormData } from '../components/AnalysisListSearchForm'
import { Column, ColumnFilterEntry, ServerSortState } from '../types/analysisList'

const SERVER_BATCH_SIZE = 1000

type ListResponse = {
  rows: Record<string, unknown>[]
  columns: Column[]
  db_columns: Column[]
  total_count: number
  // キャンペーンポイント機能が有効か(紹介者一覧のみ返す。サーバの設定行の有無で判定)
  campaign_point_enabled?: boolean
}

interface UseAnalysisListOptions {
  client: Client
  storageKeyPrefix: string
  fetchListKey: (
    clientId: number,
    page: number,
    queryFilter: Record<string, unknown>,
  ) => [string, Record<string, unknown>]
  // CSV エクスポート用の signed URL を取得する関数
  exporter: (clientId: number, queryFilter: Record<string, unknown>) => Promise<string>
}

// アクティブなカラムフィルタだけをサーバ送信用に直列化
const serializeColumnFilters = (
  filters: Record<string, ColumnFilterEntry>,
): string => {
  const payload = Object.entries(filters)
    .filter(([, entry]) => isFilterActive(entry.filter))
    .map(([field, entry]) => ({
      field,
      dataType: entry.dataType,
      combinator: entry.filter.combinator,
      conditions: entry.filter.conditions,
    }))
  return payload.length === 0 ? '' : JSON.stringify(payload)
}

export function useAnalysisList({
  client,
  storageKeyPrefix,
  fetchListKey,
  exporter,
}: UseAnalysisListOptions) {
  // 検索フォーム由来の絞り込み条件
  const [filter, setFilter] = useState<Record<string, string>>({})
  // DataTable のカラム単位フィルタ
  const [columnFilters, setColumnFilters] = useState<Record<string, ColumnFilterEntry>>({})
  const [debouncedColumnFilters] = useDebouncedValue(columnFilters, 500)
  // サーバサイドソート
  const [sortState, setSortState] = useState<ServerSortState>(null)
  // ページング
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  // 1ページ分の表示行がサーバの 1000 件バッチに跨る場合は2バッチ取得する
  const startIdx = (page - 1) * perPage
  const endIdx = startIdx + perPage - 1
  const batch1 = Math.floor(startIdx / SERVER_BATCH_SIZE)
  const batch2 = Math.floor(endIdx / SERVER_BATCH_SIZE)
  const needsBatch2 = batch1 !== batch2

  const fetchFilter = useMemo(() => ({
    ...filter,
    sort_field: sortState?.field ?? '',
    sort_direction: sortState?.direction ?? '',
    column_filters: serializeColumnFilters(debouncedColumnFilters),
  }), [filter, sortState, debouncedColumnFilters])

  const { data: list, isValidating: isValidatingBatch1, error: errorBatch1 } = useSWRImmutable<ListResponse>(
    fetchListKey(client.id, batch1, fetchFilter),
    fetcherWithAuthPost,
    { keepPreviousData: true },
  )
  const { data: list2, isValidating: isValidatingBatch2, error: errorBatch2 } = useSWRImmutable<ListResponse>(
    needsBatch2 ? fetchListKey(client.id, batch2, fetchFilter) : null,
    fetcherWithAuthPost,
    { keepPreviousData: true },
  )
  const isLoading = isValidatingBatch1 || (needsBatch2 && isValidatingBatch2)
  const fetchError = errorBatch1 || errorBatch2

  const combinedRows = useMemo(() => {
    const rows1 = list?.rows ?? []
    const rows2 = needsBatch2 ? (list2?.rows ?? []) : []
    return [...rows1, ...rows2]
  }, [list, list2, needsBatch2])

  const sliceOffset = startIdx - batch1 * SERVER_BATCH_SIZE

  const crmColumns = list?.columns ?? []
  const dbColumns = list?.db_columns ?? []

  const crmStorageKey = `${storageKeyPrefix}:selectedColumns:${client.id}`
  const dbStorageKey = `${storageKeyPrefix}:db_selectedColumns:${client.id}`

  const [selectedCrm, setSelectedCrm] = useState<string[]>(() =>
    loadFields(crmStorageKey, crmColumns.map(c => c.field)),
  )
  const [selectedDb, setSelectedDb] = useState<string[]>(() =>
    loadFields(dbStorageKey, dbColumns.map(c => c.field)),
  )

  // client 切替時に1度だけ localStorage から復元する
  const crmInitializedFor = useRef<number | null>(null)
  const dbInitializedFor = useRef<number | null>(null)
  useEffect(() => {
    if (crmInitializedFor.current === client.id) return
    if (crmColumns.length === 0) return
    setSelectedCrm(loadFields(crmStorageKey, crmColumns.map(c => c.field)))
    crmInitializedFor.current = client.id
  }, [crmColumns, crmStorageKey, client.id])
  useEffect(() => {
    if (dbInitializedFor.current === client.id) return
    if (dbColumns.length === 0) return
    setSelectedDb(loadFields(dbStorageKey, dbColumns.map(c => c.field)))
    dbInitializedFor.current = client.id
  }, [dbColumns, dbStorageKey, client.id])

  // 検索フォーム用の重複なし全カラム
  const allColumns = useMemo(() => {
    const seen = new Set<string>()
    const merged: Column[] = []
    for (const c of [...crmColumns, ...dbColumns]) {
      // 並べ替え・絞り込みできない列は検索条件に出さない
      if (c.filterable === false) continue
      if (seen.has(c.field)) continue
      seen.add(c.field)
      merged.push(c)
    }
    return merged
  }, [crmColumns, dbColumns])

  // 実際にテーブル表示するカラム（選択済みのみ）
  const filterableColumns = useMemo(() => [
    ...crmColumns.filter(c => selectedCrm.includes(c.field)),
    ...dbColumns.filter(c => selectedDb.includes(c.field)),
  ], [crmColumns, dbColumns, selectedCrm, selectedDb])

  const handleSubmit = async (values: FormData) => {
    const selectedColumn = allColumns.find(c => c.field === values.column)
    setFilter({
      date: values.date,
      from: values.startDate ?? '',
      to: values.endDate ?? '',
      column: values.column ?? '',
      column_type: selectedColumn?.cellDataType ?? '',
      condition: values.condition ?? '',
      query: values.value,
      query1: values.value2,
    })
    setPage(1)
  }

  const handleChangeSort = (next: ServerSortState) => {
    setSortState(next)
    setPage(1)
  }

  // 表示対象から外れたカラムのソートを自動解除
  useEffect(() => {
    if (!sortState) return
    const visibleFields = new Set(filterableColumns.map(c => c.field))
    if (!visibleFields.has(sortState.field)) {
      setSortState(null)
      setPage(1)
    }
  }, [filterableColumns, sortState])

  // 表示対象から外れたカラムのフィルタを自動解除
  useEffect(() => {
    const visibleFields = new Set(filterableColumns.map(c => c.field))
    setColumnFilters(prev => {
      const next: Record<string, ColumnFilterEntry> = {}
      let changed = false
      for (const [field, entry] of Object.entries(prev)) {
        if (visibleFields.has(field)) next[field] = entry
        else changed = true
      }
      return changed ? next : prev
    })
  }, [filterableColumns])

  const handleChangeColumnFilters = useCallback(
    (updater: (prev: Record<string, ColumnFilterEntry>) => Record<string, ColumnFilterEntry>) => {
      setColumnFilters(updater)
    },
    [],
  )

  // カラムフィルタ変更時はページを1に戻す
  useEffect(() => {
    setPage(1)
  }, [debouncedColumnFilters])

  const [isExporting, setIsExporting] = useState(false)
  const [csvError, setCsvError] = useState<string | null>(null)

  const handleExportCsv = async () => {
    setCsvError(null)
    setIsExporting(true)
    try {
      const url = await exporter(client.id, fetchFilter)
      window.location.href = url
    } catch (e) {
      console.error('CSVダウンロードに失敗しました', e)
      setCsvError('CSVダウンロードに失敗しました')
    } finally {
      setIsExporting(false)
    }
  }

  const handleChangePerPage = (v: number) => {
    setPerPage(v)
    setPage(1)
  }

  return {
    // データ
    combinedRows,
    sliceOffset,
    totalCount: list?.total_count ?? 0,
    campaignPointEnabled: list?.campaign_point_enabled ?? false,
    crmColumns,
    dbColumns,
    allColumns,
    filterableColumns,
    fetchError,
    isLoading,

    // localStorage キー
    crmStorageKey,
    dbStorageKey,

    // 列選択
    selectedCrm,
    setSelectedCrm,
    selectedDb,
    setSelectedDb,

    // ページング
    page,
    setPage,
    perPage,
    handleChangePerPage,

    // ソート
    sortState,
    handleChangeSort,

    // カラム単位フィルタ
    columnFilters,
    handleChangeColumnFilters,

    // 検索送信
    handleSubmit,

    // CSV エクスポート
    isExporting,
    csvError,
    handleExportCsv,
  }
}
